<script setup>
import { useHead } from "#imports";
import { useIdle } from "@vueuse/core";
import { computed, h, nextTick, onMounted, ref, watch } from "vue";
import VoixVideoControls from "./VoixVideoControls.vue";
const props = defineProps({
  src: { type: String, required: true },
  options: { type: Object, required: true },
  width: { type: null, required: true },
  height: { type: null, required: true }
});
let player = {};
const paused = ref(true);
const muted = ref(false);
const volume = ref(0);
const showControls = ref(false);
const { idle, reset } = useIdle(1e3);
const wistiaId = computed(() => {
  const regex = /wistia\.com\/(?:embed\/)?medias\/([a-zA-Z0-9]+)/;
  const match = props.src.match(regex);
  return match ? match[1] : void 0;
});
const wistiaControls = computed(() => {
  return props.options.controls.mode === "wistia" && props.options.controls.display;
});
const videoStyles = computed(() => {
  const styles = {
    aspectRatio: props.options.style.aspectRatio ? `aspect-ratio: ${props.options.style.aspectRatio}; ` : "",
    width: props.options.style.respectWidth ? `width: ${props.width}px; ` : "",
    height: props.options.style.respectHeight ? `height: ${props.height}px; ` : ""
  };
  return styles.aspectRatio + styles.width + styles.height;
});
function play() {
  paused.value = false;
  player.play();
}
function loop() {
  player.bind("end", play);
}
function pause() {
  paused.value = true;
  player.pause();
}
function mute() {
  muted.value = true;
  player.mute();
}
function unMute() {
  muted.value = false;
  player.unmute();
}
function setVolume({ value, init = false }) {
  volume.value = value;
  player.volume(value / 100);
  if (!init && Number(value) === 0)
    mute();
  else
    unMute();
}
function fullscreen() {
  player.requestFullscreen();
}
watch(idle, (value) => {
  if (value) {
    showControls.value = false;
  } else {
    showControls.value = true;
  }
});
onMounted(async () => {
  const WistiaPlayer = new Promise((resolve) => {
    const checkWistia = () => {
      if (typeof Wistia === "object" && Wistia.api(wistiaId.value) !== null) {
        resolve();
      } else {
        setTimeout(checkWistia, 1e3);
      }
    };
    checkWistia();
  });
  await WistiaPlayer.then(() => {
    player = Wistia.api(wistiaId.value);
    setVolume({ value: props.options.volume, init: true });
    if (props.options.mute)
      mute();
    if (props.options.autoplay) {
      play();
    }
    if (props.options.loop)
      loop();
  });
});
useHead({
  script: [
    {
      src: "https://fast.wistia.net/assets/external/E-v1.js",
      async: true
    }
  ]
});
</script>

<template>
  <div class="relative">
    <div :style="videoStyles" v-bind="$attrs">
      <div
        class="wistia_embed"
        :class="`wistia_async_${wistiaId} controlsVisibleOnLoad=${wistiaControls} playbar=${wistiaControls} playButton=${wistiaControls} smallPlayButton=${wistiaControls} settingsControl=${wistiaControls} volume=${options.volume} volumeControl=${wistiaControls} muted=${options.mute} fullscreenButton=${wistiaControls && options.fullscreen} autoplay=${options.autoplay} playsinline=true bg-black`"
        style="width: 100%; height: 100%;"
      />
    </div>

    <slot v-if="options.controls.mode === 'voix' && options.controls.display" :play="play" :pause="pause" :paused="paused" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen">
      <div
        class="flex absolute top-0 left-0 h-full w-full text-white duration-200"
        :class="{ 'opacity-0': !showControls, 'opacity-100': showControls || paused }"
        @mouseenter="showControls = true;
  reset()"
        @mousemove="showControls = true;
  reset()"
        @mouseleave="showControls = false"
      >
        <VoixVideoControls :paused="paused" :play="play" :pause="pause" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen" :controls-position="options.controls.position" />
      </div>
    </slot>
  </div>
</template>

<style scoped>
*{border-radius:0!important}
</style>
